import { TOTAL_ONBOARDING_STEPS } from '@/constants/onboarding';
import { useAppDispatch, useAppSelector } from '@/state/hooks';
import {
    completeStep,
    resetOnboarding,
    selectCompletionPercentage,
    selectCurrentStep,
    selectIsOnboardingActive,
    setCurrentStep,
    setFirstTimeUser,
    skipStep,
    toggleOnboarding
} from '@/state/reducers/onboardingSlice';
import { useCallback } from 'react';

interface UseTourManagerReturn {
    currentStep: number;
    isActive: boolean;
    progress: number;
    goToStep: (step: number) => void;
    nextStep: () => void;
    previousStep: () => void;
    skipCurrentStep: () => void;
    skipTour: () => void;
    completeTour: () => void;
    replayTour: () => void;
    toggleTour: (active: boolean) => void;
}

const useTourManager = (): UseTourManagerReturn => {
    const dispatch = useAppDispatch();
    const currentStep = useAppSelector(selectCurrentStep);
    const isActive = useAppSelector(selectIsOnboardingActive);
    const progress = useAppSelector(selectCompletionPercentage);

    const goToStep = useCallback(
        (step: number) => {
            dispatch(setCurrentStep(step));
        },
        [dispatch]
    );

    const nextStep = useCallback(() => {
        dispatch(completeStep(currentStep));
        dispatch(setCurrentStep(currentStep + 1));
    }, [dispatch, currentStep]);

    const previousStep = useCallback(() => {
        if (currentStep > 0) {
            dispatch(setCurrentStep(currentStep - 1));
        }
    }, [dispatch, currentStep]);

    const skipCurrentStep = useCallback(() => {
        dispatch(setFirstTimeUser(false));
        dispatch(skipStep(currentStep));
        if (currentStep >= TOTAL_ONBOARDING_STEPS - 1) {
            dispatch(toggleOnboarding(false));
            return;
        }

        dispatch(setCurrentStep(currentStep + 1));
    }, [dispatch, currentStep]);

    const skipTour = useCallback(() => {
        dispatch(setFirstTimeUser(false));
        dispatch(skipStep(currentStep));
        dispatch(toggleOnboarding(false));
    }, [dispatch, currentStep]);

    const completeTour = useCallback(() => {
        dispatch(completeStep(currentStep));
        dispatch(toggleOnboarding(false));
    }, [dispatch, currentStep]);

    const replayTour = useCallback(() => {
        dispatch(resetOnboarding());
        dispatch(setFirstTimeUser(false));
        dispatch(toggleOnboarding(true));
    }, [dispatch]);

    const toggleTour = useCallback(
        (active: boolean) => {
            dispatch(toggleOnboarding(active));
        },
        [dispatch]
    );

    return {
        currentStep,
        isActive,
        progress,
        goToStep,
        nextStep,
        previousStep,
        skipCurrentStep,
        skipTour,
        completeTour,
        replayTour,
        toggleTour
    };
};

export default useTourManager;
